
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { 
  Mail, 
  Phone, 
  MapPin, 
  Clock, 
  ArrowUpRight, 
  CheckCircle2, 
  Sparkles,
  MessageSquare,
  Send
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";
import ContactForm from "@/components/ContactForm";
import PageTransition from "@/components/PageTransition";
import SEO from "@/components/SEO";
import Image from "@/components/Image";

const Contact = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUrl = `https://losangelesseogroup.com${location.pathname}`;
  const [formSuccess, setFormSuccess] = useState(false);

  const handleFormSuccess = () => {
    setFormSuccess(true);
    toast({
      title: "Form Submitted Successfully",
      description: "Your message has been sent to our team. We'll get back to you shortly!",
    });
    
    // Set session storage to indicate form was submitted
    sessionStorage.setItem("formSubmitted", "true");
    // Navigate to thank you page
    navigate("/thank-you");
  };

  // Animation variants
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };
  
  const staggerContainer = {
    initial: { opacity: 0 },
    animate: { 
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };
  
  const staggerItem = {
    initial: { opacity: 0, y: 15 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <PageTransition>
      <SEO 
        seo={{
          title: "Contact Los Angeles SEO Group | Get in Touch | Los Angeles SEO Company",
          description: "Contact the leading SEO company in Los Angeles. Get in touch with Los Angeles SEO Group for a free consultation and discover how we can help grow your business online.",
          jsonLd: {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            name: "Los Angeles SEO Services",
            image: "https://losangelesseogroup.com/og-image.png",
            "@id": "https://losangelesseogroup.com/",
            url: "https://losangelesseogroup.com/",
            telephone: "+1-323-784-1577",
            address: {
              "@type": "PostalAddress",
              streetAddress: "10000 Washington Blvd",
              addressLocality: "Culver City",
              addressRegion: "CA",
              postalCode: "90232",
              addressCountry: "US",
            },
            areaServed: ["Culver City", "West Los Angeles", "Santa Monica", "Mar Vista"],
            priceRange: "$$",
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: "4.9",
              reviewCount: "127",
            },
          },
        }}
        url={currentUrl}
      />

      <Navbar />
      
      {/* Hero Section - Redesigned with less space at top */}
      <section className="relative pt-20 pb-12 bg-gradient-to-b from-background to-muted/10 overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-[0.02] pointer-events-none" />
        
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-desert-sunset via-desert-canyon to-desert-sky" />
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-desert-sunset/5 blur-[100px]" />
        <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full bg-desert-sky/5 blur-[100px]" />
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8 max-w-6xl mx-auto">
            <motion.div 
              className="md:max-w-xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <motion.div 
                className="inline-flex items-center px-3 py-1 rounded-full bg-desert-sunset/10 text-desert-sunset text-sm font-medium mb-4"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: 0.1 }}
              >
                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                <span>We respond within 24 hours</span>
              </motion.div>
              
              <motion.h1 
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 tracking-tight"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.05 }}
              >
                Let's Talk About <span className="text-gradient bg-canyon-gradient">Your Success</span>
              </motion.h1>
              
              <motion.p 
                className="text-base md:text-lg text-foreground/70 mb-6 leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                Ready to elevate your online presence? Our SEO experts are here to help you achieve measurable results and sustainable growth.
              </motion.p>
              
              <motion.div
                className="flex flex-wrap gap-2 mb-6"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.15 }}
              >
                {[
                  { icon: <CheckCircle2 className="w-4 h-4 text-desert-sunset" />, text: "Free initial consultation" },
                  { icon: <CheckCircle2 className="w-4 h-4 text-desert-canyon" />, text: "Customized SEO strategies" },
                  { icon: <CheckCircle2 className="w-4 h-4 text-desert-sky" />, text: "Transparent reporting" }
                ].map((item, i) => (
                  <div key={i} className="flex items-center px-3 py-1.5 bg-muted/50 rounded-lg text-sm">
                    {item.icon}
                    <span className="ml-1.5">{item.text}</span>
                  </div>
                ))}
              </motion.div>
            </motion.div>
            
            <motion.div
              className="w-full md:w-auto flex justify-center"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-desert-sunset via-desert-canyon to-desert-sky rounded-xl opacity-30 blur-lg" />
                <div className="relative bg-background/80 backdrop-blur-sm border border-desert-sunset/20 rounded-xl p-3 shadow-lg">
                  <img 
                    src="/placeholder.svg" 
                    alt="Contact Los Angeles SEO Group" 
                    className="w-full max-w-[280px] h-auto rounded-lg object-cover"
                    style={{ aspectRatio: "4/3" }}
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Main Content - Redesigned layout */}
      <section className="py-12 relative">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Contact Form Section - 8/12 width on desktop */}
            <motion.div 
              className="lg:col-span-8"
              variants={fadeIn}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
            >
              <div className="bg-background border border-border shadow-sm rounded-xl overflow-hidden relative">
                {/* Decorative accent */}
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-desert-sunset via-desert-canyon to-desert-sky" />
                
                <div className="p-6 md:p-8">
                  <div className="flex items-center mb-6">
                    <div className="p-2.5 rounded-md bg-desert-sunset/10 text-desert-sunset mr-3">
                      <MessageSquare className="w-5 h-5" />
                    </div>
                    <h2 className="text-2xl font-bold">Send Us a Message</h2>
                  </div>
                  
                  {formSuccess && (
                    <Alert className="mb-6 bg-desert-cactus/10 border-desert-cactus">
                      <CheckCircle2 className="h-4 w-4 text-desert-cactus" />
                      <AlertTitle className="text-desert-cactus ml-2">Message Sent!</AlertTitle>
                      <AlertDescription>
                        Your message has been received. We'll get back to you as soon as possible.
                      </AlertDescription>
                    </Alert>
                  )}
                  
                  <ContactForm onSuccess={handleFormSuccess} />
                </div>
              </div>
            </motion.div>
            
            {/* Contact Information Section - 4/12 width on desktop */}
            <motion.div 
              variants={staggerContainer}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              className="lg:col-span-4 space-y-6"
            >
              {/* Contact Info Card - Redesigned */}
              <div className="bg-background border border-border shadow-sm rounded-xl overflow-hidden relative">
                {/* Decorative accent */}
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-desert-sky via-desert-canyon to-desert-sunset" />
                
                <div className="p-6">
                  <h2 className="text-xl font-bold mb-5 flex items-center">
                    <Send className="w-4 h-4 mr-2 text-desert-sky" />
                    Contact Information
                  </h2>
                  
                  <div className="space-y-4">
                    <motion.div variants={staggerItem} className="flex items-start gap-3 group">
                      <div className="p-2.5 rounded-md bg-desert-sunset/10 text-desert-sunset">
                        <Mail className="w-4 h-4" />
                      </div>
                      <div>
                        <h3 className="font-medium text-sm">Email Us</h3>
                        <a 
                          href="mailto:info@losangelesseogroup.com" 
                          className="text-foreground/70 mt-0.5 block text-sm hover:text-desert-sunset transition-colors"
                        >
                          info@losangelesseogroup.com
                        </a>
                      </div>
                    </motion.div>
                    
                    <motion.div variants={staggerItem} className="flex items-start gap-3 group">
                      <div className="p-2.5 rounded-md bg-desert-sky/10 text-desert-sky">
                        <Phone className="w-4 h-4" />
                      </div>
                      <div>
                        <h3 className="font-medium text-sm">Call Us</h3>
                        <a 
                          href="tel:3237841577" 
                          className="text-foreground/70 mt-0.5 block text-sm hover:text-desert-sky transition-colors"
                        >
                          (323) 784-1577
                        </a>
                        <p className="text-foreground/50 text-xs">Mon-Fri, 9am-6pm PST</p>
                      </div>
                    </motion.div>
                    
                    <motion.div variants={staggerItem} className="flex items-start gap-3 group">
                      <div className="p-2.5 rounded-md bg-desert-cactus/10 text-desert-cactus">
                        <MapPin className="w-4 h-4" />
                      </div>
                      <div>
                        <h3 className="font-medium text-sm">Visit Us</h3>
                        <a 
                          href="https://maps.google.com/?q=10000+Washington+Blvd,Culver+City,CA+90232" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-foreground/70 mt-0.5 block text-sm hover:text-desert-cactus transition-colors"
                        >
                          10000 Washington Blvd<br />
                          Culver City, CA 90232
                        </a>
                      </div>
                    </motion.div>
                    
                    <motion.div variants={staggerItem} className="flex items-start gap-3 group">
                      <div className="p-2.5 rounded-md bg-desert-canyon/10 text-desert-canyon">
                        <Clock className="w-4 h-4" />
                      </div>
                      <div>
                        <h3 className="font-medium text-sm">Business Hours</h3>
                        <p className="text-foreground/70 mt-0.5 text-xs">
                          Monday-Friday: 9am-6pm PST<br />
                          Saturday-Sunday: Closed
                        </p>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </div>
              
              {/* Social Media Card - Redesigned */}
              <motion.div 
                variants={fadeIn}
                className="bg-background border border-border shadow-sm rounded-xl overflow-hidden relative"
              >
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-desert-canyon to-desert-sky" />
                
                <div className="p-6">
                  <h2 className="text-xl font-bold mb-4">Connect With Us</h2>
                  
                  <div className="flex gap-3">
                    {[
                      { name: "Facebook", icon: "F", href: "#", color: "bg-blue-500" },
                      { name: "Twitter", icon: "T", href: "#", color: "bg-sky-500" },
                      { name: "LinkedIn", icon: "L", href: "#", color: "bg-blue-700" },
                      { name: "Instagram", icon: "I", href: "#", color: "bg-pink-500" },
                    ].map((social, index) => (
                      <motion.a
                        key={index}
                        href={social.href}
                        className={`rounded-md p-2.5 text-white ${social.color} hover:shadow-md transition-all`}
                        aria-label={`Follow us on ${social.name}`}
                        whileHover={{ y: -3, scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <span className="sr-only">Follow us on {social.name}</span>
                        <div className="w-4 h-4 flex items-center justify-center font-bold text-xs">
                          {social.icon}
                        </div>
                      </motion.a>
                    ))}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Map Section - Redesigned */}
      <section className="py-12 bg-muted/20">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div 
            className="flex flex-col md:flex-row md:items-center md:justify-between max-w-6xl mx-auto mb-8"
            variants={fadeIn}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <div>
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-desert-sky/10 text-desert-sky text-sm font-medium mb-2">
                <MapPin className="w-3.5 h-3.5 mr-1.5" />
                <span>Our Location</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold">
                Visit Our <span className="text-gradient bg-sunset-gradient">Office</span>
              </h2>
            </div>
            <p className="text-foreground/70 mt-2 md:mt-0 md:text-right max-w-md">
              Conveniently located in the heart of Los Angeles, near major business districts and creative hubs.
            </p>
          </motion.div>
          
          <motion.div 
            className="bg-background border border-border shadow-md rounded-xl overflow-hidden"
            variants={fadeIn}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <div className="aspect-[16/9] relative">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3306.0!2d-118.3908!3d34.0262!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c2ba8db1c6a0a1%3A0x1!2s10000%20Washington%20Blvd%2C%20Culver%20City%2C%20CA%2090232!5e0!3m2!1sen!2sus!4v1688420778736!5m2!1sen!2sus"
                width="100%" 
                height="100%" 
                style={{ border: 0 }} 
                allowFullScreen={false} 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="Los Angeles SEO Group Office Location"
                className="absolute inset-0"
              />
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* FAQ Section - Redesigned */}
      <section className="py-16 bg-background relative">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div 
            className="max-w-3xl mx-auto text-center mb-10"
            variants={fadeIn}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-desert-canyon/10 text-desert-canyon text-sm font-medium mb-3">
              FAQ
            </div>
            <h2 className="text-2xl md:text-3xl font-bold mb-3">
              Have <span className="text-gradient bg-sky-gradient">Questions?</span>
            </h2>
            <p className="text-foreground/70">
              Find answers to our most commonly asked questions
            </p>
          </motion.div>
          
          <motion.div 
            className="max-w-3xl mx-auto grid gap-4 md:grid-cols-2"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {[
              {
                question: "How soon can we start working together?",
                answer: "Once we've had an initial consultation and agreed on the scope of work, we can typically begin within 1-2 weeks. For urgent projects, we may be able to accommodate a faster timeline."
              },
              {
                question: "Do you require long-term contracts?",
                answer: "While SEO is a long-term strategy and best results are seen with consistent effort over time, we offer flexible engagement options. We typically recommend a minimum 6-month commitment for optimal results."
              },
              {
                question: "What information do you need to provide a quote?",
                answer: "To provide an accurate quote, we'll need to know your website URL, business goals, target audience, competitive landscape, and any specific SEO challenges you're facing."
              },
              {
                question: "How do you report on progress and results?",
                answer: "We provide comprehensive monthly reports detailing all key metrics including rankings, traffic, conversions, and completed work. We also schedule regular calls to review progress."
              },
            ].map((faq, index) => (
              <motion.div
                key={index}
                variants={staggerItem}
                className="bg-background border border-border hover:border-desert-sunset/30 hover:shadow-sm rounded-xl p-5 transition-all duration-300"
                whileHover={{ y: -2 }}
              >
                <h3 className="text-lg font-bold mb-2 group-hover:text-desert-sunset transition-colors">
                  {faq.question}
                </h3>
                <p className="text-foreground/70 text-sm leading-relaxed">{faq.answer}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
      
      <Footer />
    </PageTransition>
  );
};

export default Contact;
